import {useState} from 'react';
import {Skeleton, view, Icon, Action, Divider} from '@sail/ui';
import {AnimatePresence, motion, tokens as motionTokens} from '@sail/motion';

import {chevronDown} from '@sail/icons/react/Icon';
import type {FormatXMLElementFn} from '@sail/intl';
import {message, useIntl} from '@sail/intl';
import {useSelector} from 'src/v2/state/store';
import {AssetWithNetworkIcon} from 'src/v2/components/common/AssetWithNetworkIcon';
import {Banner} from 'src/v2/components/common/Banner';
import {Text} from 'src/v2/components/common/Text';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';
import {getNetworkName, getNetworkIcon} from 'src/components/Wallet/constants';
import {getCurrencyIcon} from 'src/v2/util/getCurrencyIcon';
import {
  NETWORK_TIMING,
  DEFAULT_TIMING,
  formatTimingRange,
} from 'src/v2/components/anyCrypto/networkTiming';
import type {SupportedNetwork} from 'src/types';
import type {OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum} from '@stripe-internal/data-v1/schema/types';

const messages = {
  youSend: message({
    id: 'crypto.payins.v2.any_crypto.deposit.you_send',
    description: 'Label above the deposit amount',
    defaultMessage: 'You send',
  }),
  amountOnNetwork: message<{
    amount: React.ReactNode;
    token: React.ReactNode;
    network: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.any_crypto.deposit.amount_on_network',
    description: 'Amount and network for the deposit',
    defaultMessage: '{amount} {token} on {network}',
  }),
  warning: message<{
    token: React.ReactNode;
    network: React.ReactNode;
    b: FormatXMLElementFn;
  }>({
    id: 'crypto.payins.v2.any_crypto.deposit.warning',
    description: 'Warning about sending correct asset',
    defaultMessage:
      'Only send <b>{token}</b> on <b>{network}</b>. Other assets may be lost.',
  }),
  transactionDetectionTimeLabel: message({
    id: 'crypto.payins.v2.any_crypto.deposit.transaction_detection_time_label',
    description: 'Label for the transaction detection time row',
    defaultMessage: 'Transaction detection time',
  }),
  processingTimeLabel: message({
    id: 'crypto.payins.v2.any_crypto.deposit.processing_time_label',
    description: 'Label for the processing time row',
    defaultMessage: 'Processing time',
  }),
  processingTimeValue: message({
    id: 'crypto.payins.v2.any_crypto.deposit.processing_time_value',
    description: 'Processing time value after transaction is detected',
    defaultMessage: '<30 seconds',
  }),
  refundAddressLabel: message({
    id: 'crypto.payins.v2.any_crypto.deposit.refund_address_label',
    description: 'Label for the refund address row',
    defaultMessage: 'Refund address',
  }),
};

const truncateAddress = (address: string) =>
  `${address.slice(0, 6)}...${address.slice(-4)}`;

export const ExpandableDepositDetails = ({
  amount,
  token,
  network,
}: {
  amount: string | null;
  token: OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum;
  network: SupportedNetwork | null;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const intl = useIntl();

  const refundMethod = useSelector(
    (state) => state.anyCryptoDeposit.refundMethod,
  );
  const customRefundAddress = useSelector(
    (state) => state.anyCryptoDeposit.customRefundAddress,
  );

  const formattedToken = formatAssetCode(token);
  const networkName = network ? getNetworkName(network) : '';
  const currencyIcon = getCurrencyIcon(token);
  const networkIcon =
    network && token !== 'sol' && token !== 'btc'
      ? getNetworkIcon(network)
      : null;

  const timing = network
    ? NETWORK_TIMING[network] ?? DEFAULT_TIMING
    : DEFAULT_TIMING;
  const detectionTimeText = formatTimingRange(timing.detectionTime, intl);

  const showRefundAddress =
    refundMethod === 'setNewRefundAddress' && !!customRefundAddress;

  return (
    <view.div
      css={{
        borderRadius: 'small',
        borderColor: 'border',
        borderWidth: 'small',
        borderStyle: 'solid',
        stack: 'y',
        gapY: isExpanded ? 'large' : '16px',
        padding: 'medium',
      }}
    >
      <Action onPress={() => setIsExpanded((v) => !v)}>
        <view.div css={{stack: 'x', gapX: 'space.150', alignY: 'center'}}>
          <AssetWithNetworkIcon
            assetIcon={currencyIcon}
            networkIcon={networkIcon ?? undefined}
          />
          <view.div css={{stack: 'y', flex: 1, minWidth: 'space.0'}}>
            <Text type="body" css={{color: 'subdued'}}>
              {intl.formatMessage(messages.youSend)}
            </Text>
            <Text type="bodyEmphasized" css={{color: 'text'}}>
              {amount ? (
                intl.formatMessage(messages.amountOnNetwork, {
                  amount,
                  token: formattedToken,
                  network: networkName,
                })
              ) : (
                <Skeleton css={{width: '150px', height: 'medium'}} />
              )}
            </Text>
          </view.div>
          <view.div
            css={{
              width: 'large',
              height: 'large',
              borderRadius: '50%',
              backgroundColor: 'offset',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon
              icon={chevronDown}
              css={{
                width: 'small',
                height: 'small',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </view.div>
        </view.div>
      </Action>

      <AnimatePresence initial={false}>
        {isExpanded ? (
          <view.div
            key="deposit-details"
            uses={[
              motion({
                initial: {height: 0, opacity: 0},
                animate: {height: 'auto', opacity: 1},
                exit: {height: 0, opacity: 0},
                transition: {
                  duration: 0.2,
                  ease: motionTokens.easing.smooth,
                },
              }),
            ]}
            css={{overflow: 'hidden'}}
          >
            <view.div css={{stack: 'y', gapY: 'space.150'}}>
              <view.div
                css={{
                  stack: 'x',
                  distribute: 'space-between',
                  alignY: 'center',
                }}
              >
                <Text type="body" css={{color: 'text'}}>
                  {intl.formatMessage(messages.transactionDetectionTimeLabel)}
                </Text>
                <Text type="bodyEmphasized" css={{color: 'text'}}>
                  {detectionTimeText}
                </Text>
              </view.div>
              <Divider />
              <view.div
                css={{
                  stack: 'x',
                  distribute: 'space-between',
                  alignY: 'center',
                }}
              >
                <Text type="body" css={{color: 'text'}}>
                  {intl.formatMessage(messages.processingTimeLabel)}
                </Text>
                <Text type="bodyEmphasized" css={{color: 'text'}}>
                  {intl.formatMessage(messages.processingTimeValue)}
                </Text>
              </view.div>
              {showRefundAddress ? (
                <>
                  <Divider />
                  <view.div
                    css={{
                      stack: 'x',
                      distribute: 'space-between',
                      alignY: 'center',
                    }}
                  >
                    <Text type="body" css={{color: 'text'}}>
                      {intl.formatMessage(messages.refundAddressLabel)}
                    </Text>
                    <Text type="bodyEmphasized" css={{color: 'text'}}>
                      {truncateAddress(customRefundAddress)}
                    </Text>
                  </view.div>
                </>
              ) : null}
            </view.div>
          </view.div>
        ) : null}
      </AnimatePresence>

      <Banner
        message={intl.formatMessage(messages.warning, {
          token: formattedToken,
          network: networkName,
          b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
        })}
        variant="warning"
      />
    </view.div>
  );
};
